import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldX, Radio, Send, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Banned() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/user/ban-status");
      setStatus(data);
    } catch { setStatus(null); }
  };
  useEffect(() => { load(); }, []);

  const isBanned = user?.is_banned || status?.is_banned;
  const reason = user?.ban_reason || status?.reason;
  const guild = user?.ban_guild_name || status?.guild_name;
  const latestAppeal = status?.latest_appeal;
  const wasAccepted = latestAppeal?.reviewed && latestAppeal?.decision === "accepted";

  // Rate-limit: hide the form if user submitted an appeal within the last 24h.
  const withinLast24h = (() => {
    if (!latestAppeal?.submitted_at) return false;
    return Date.now() - new Date(latestAppeal.submitted_at).getTime() < 24 * 60 * 60 * 1000;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 20) {
      toast.error("Please write at least a couple of sentences.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/user/ban-appeal", { message: message.trim() });
      setJustSubmitted(true);
      setMessage("");
      await load();
      toast.success("Appeal sent — an admin will review it shortly.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16" data-testid="banned-page">
      <div className={`max-w-2xl w-full surface-card p-12 ${isBanned ? "border-[var(--ff-red)]" : "border-[#32D74B]"}`}>
        {!isBanned && wasAccepted ? (
          <div className="text-center" data-testid="ban-lifted">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#32D74B] flex items-center justify-center">
              <CheckCircle2 size={44} className="text-black" />
            </div>
            <div className="label-eyebrow text-[#32D74B] mb-3">▮ WELCOME BACK</div>
            <h1 className="font-display text-4xl lg:text-5xl mb-4 leading-none">YOUR BAN WAS LIFTED.</h1>
            <p className="text-[var(--ff-text-dim)] text-sm mt-6 max-w-lg mx-auto">
              Your appeal was accepted. Every fan feature is back on for you — watchlist, DM reminders, host studio if applicable.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/watchlist" className="btn-primary text-xs" data-testid="ban-lifted-watchlist-btn">Go to My Watchlist →</Link>
              <Link to="/" className="btn-ghost text-xs">Home</Link>
            </div>
          </div>
        ) : (
          <>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-[var(--ff-red)] flex items-center justify-center">
            <ShieldX size={44} className="text-white" />
          </div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-3">▮ ACCESS REVOKED</div>
          <h1 className="font-display text-4xl lg:text-5xl mb-4 leading-none">YOU&apos;RE BANNED FROM THE NETWORK.</h1>
          <p className="text-[var(--ff-text-dim)] text-sm mt-6 max-w-lg mx-auto">
            Your Discord account has been removed from the Freshy Fresh Productions server and blocked from all connected fan features.
          </p>
        </div>

        {isBanned && (
          <div className="mt-8 p-6 bg-[var(--ff-bg)] border border-[var(--ff-red)] text-left space-y-3" data-testid="ban-details">
            {guild && (
              <div>
                <div className="label-eyebrow text-[var(--ff-red)]">▮ TRIGGER</div>
                <div className="font-mono-ui text-sm text-white mt-1">Member of blacklisted server: <span className="font-bold">{guild}</span></div>
              </div>
            )}
            {reason && (
              <div>
                <div className="label-eyebrow">▮ REASON</div>
                <div className="font-mono-ui text-xs text-[var(--ff-text-dim)] mt-1">{reason}</div>
              </div>
            )}
          </div>
        )}

        {/* Appeal form */}
        {isBanned && (
          <div className="mt-8 pt-6 border-t border-[var(--ff-border)]" data-testid="appeal-section">
            <div className="label-eyebrow mb-2 text-white">▮ REQUEST A REVIEW</div>
            <p className="text-xs text-[var(--ff-text-dim)] mb-4">
              Explain why you believe this ban was applied in error. An admin will get your message on Discord and follow up if the ban is lifted.
            </p>
            {justSubmitted || (latestAppeal && !latestAppeal.reviewed && withinLast24h) ? (
              <div className="p-5 border border-[#32D74B] bg-[#32D74B]/10 flex items-start gap-3" data-testid="appeal-submitted">
                <CheckCircle2 size={20} className="text-[#32D74B] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-mono-ui text-sm text-white">Appeal received.</div>
                  <div className="text-xs text-[var(--ff-text-dim)] mt-1">
                    Submitted {latestAppeal ? new Date(latestAppeal.submitted_at).toLocaleString() : "just now"}. Admin has been notified. You can submit another appeal in 24 hours if you don&apos;t hear back.
                  </div>
                </div>
              </div>
            ) : latestAppeal && latestAppeal.reviewed ? (
              <div className="p-5 border border-[var(--ff-border)] bg-[var(--ff-bg)] flex items-start gap-3" data-testid="appeal-reviewed">
                <CheckCircle2 size={20} className="text-white flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-mono-ui text-sm text-white">Your last appeal has been reviewed.</div>
                  <div className="text-xs text-[var(--ff-text-dim)] mt-1">
                    If your ban is still in place, feel free to send another appeal below.
                  </div>
                </div>
              </div>
            ) : null}
            {(!justSubmitted && (!latestAppeal || !withinLast24h || latestAppeal.reviewed)) && (
              <form onSubmit={submit} className="mt-4 space-y-3">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={2000}
                  placeholder="I believe this ban was applied in error because…"
                  className="w-full px-4 py-3 text-sm font-mono-ui leading-relaxed"
                  data-testid="appeal-message-input"
                />
                <div className="flex items-center justify-between">
                  <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                    {message.length}/2000 · Min 20 characters
                  </div>
                  <button type="submit" disabled={submitting || message.trim().length < 20} className="btn-primary text-xs" data-testid="appeal-submit-btn">
                    <Send size={12} /> {submitting ? "Sending…" : "Send Appeal"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to="/" className="btn-ghost text-xs" data-testid="banned-home-link">
            <Radio size={12} /> Back to public site
          </Link>
          <button onClick={async () => { await logout(); window.location.href = "/"; }} className="btn-primary text-xs" data-testid="banned-signout-btn">
            Sign Out
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
